import type { SpecPoll, SyncrelEntity, SyncrelState } from "../domain/types";
import { frameworkViewState } from "../domain/framework-scope";
import { escapeHtml } from "./render-utils";

export const renderSpecEditor = (
  state: SyncrelState,
  outlineExpanded = false,
  viewerId = "",
  canEdit = true,
): string => {
  const view = frameworkViewState(state);
  const entity = view.workspace.selectedEntityId
    ? view.entities.find((item) => item.id === view.workspace.selectedEntityId) ?? null
    : null;
  if (!entity) {
    return `
      <section class="panel panel--spec">
        <div class="panel-head"><h2>Spec Document</h2></div>
        <div class="spec-empty">
          <strong>No entity selected</strong>
          <span>Create or select an entity to edit its spec from the same graph used by the map and grid.</span>
          <button type="button" data-add-entity title="Create feature" aria-label="Create feature">+</button>
        </div>
      </section>
    `;
  }

  const doc = entity.doc.trim() ? entity.doc : defaultSpec(entity);
  const wordCount = wordsIn(doc);
  const outlineId = `spec-outline-${escapeHtml(entity.id)}`;
  return `
    <section class="panel panel--spec panel--spec-immersive">
      <div class="spec-workspace" ${outlineExpanded ? 'data-spec-outline-expanded="true"' : 'data-spec-outline-collapsed="true"'}>
        <aside class="spec-outline" id="${outlineId}" data-spec-outline-panel aria-label="Document outline" aria-hidden="${outlineExpanded ? "false" : "true"}">
          <strong>Outline</strong>
          <div>${renderOutline(doc)}</div>
        </aside>
        <section class="spec-editor-pane" aria-label="Spec writing surface">
          <div class="spec-page">
            <div class="spec-page__header">
              <input
                class="spec-title-inline"
                type="text"
                data-spec-title="${escapeHtml(entity.id)}"
                value="${escapeHtml(entity.name)}"
                aria-label="Spec title"
                autocomplete="off"
                ${canEdit ? "" : "readonly"}
              />
              <div class="spec-writing-meta">
                <span>${wordCount} words</span>
                <span class="status-chip">${escapeHtml(entity.status)}</span>
              </div>
              <div class="spec-page__actions">
                <button
                  type="button"
                  class="spec-outline-toggle ${outlineExpanded ? "is-active" : ""}"
                  data-toggle-spec-outline
                  aria-controls="${outlineId}"
                  aria-expanded="${outlineExpanded ? "true" : "false"}"
                  title="${outlineExpanded ? "Hide document outline" : "Show document outline"}"
                >Outline</button>
                ${canEdit ? `
                  <button type="button" data-spec-insert="heading">H2</button>
                  <button type="button" data-spec-insert="bullet">List</button>
                  <button type="button" data-spec-insert="decision">Decision</button>
                  <button type="button" data-spec-insert="question">Question</button>
                  <button type="button" data-add-spec-poll="${escapeHtml(entity.id)}">Poll</button>
                ` : ""}
              </div>
            </div>
            <div class="spec-writing-surface">
              <textarea
                data-spec-doc="${escapeHtml(entity.id)}"
                spellcheck="true"
                autocomplete="off"
                autocapitalize="sentences"
                rows="12"
                placeholder="Write the spec. Start with context, decision, constraints, and open questions."
                ${canEdit ? "" : "readonly"}
              >${escapeHtml(doc)}</textarea>
            </div>
            ${renderSpecPolls(entity, viewerId, canEdit)}
          </div>
        </section>
      </div>
    </section>
  `;
};

const wordsIn = (doc: string): number => (
  doc.trim() ? doc.trim().split(/\s+/).length : 0
);

const renderSpecPolls = (entity: SyncrelEntity, viewerId: string, canEdit: boolean): string => {
  const polls = entity.polls ?? [];
  if (polls.length === 0) return "";
  return `
    <div class="spec-polls" aria-label="Spec polls">
      ${polls.map((poll) => renderSpecPoll(entity.id, poll, viewerId, canEdit)).join("")}
    </div>
  `;
};

const renderSpecPoll = (entityId: string, poll: SpecPoll, viewerId: string, canEdit: boolean): string => {
  const totalVotes = poll.votes.length;
  return `
    <article class="spec-poll" data-spec-poll="${escapeHtml(poll.id)}">
      <div class="spec-poll__head">
        <span>Poll</span>
        ${canEdit ? `<button
            type="button"
            data-remove-spec-poll="${escapeHtml(poll.id)}"
            data-spec-poll-entity="${escapeHtml(entityId)}"
            title="Remove poll"
            aria-label="Remove poll"
          >Remove</button>` : ""}
      </div>
      <input
        class="spec-poll__question"
        data-spec-poll-question="${escapeHtml(poll.id)}"
        data-spec-poll-entity="${escapeHtml(entityId)}"
        value="${escapeHtml(poll.question)}"
        aria-label="Poll question"
        ${canEdit ? "" : "readonly"}
      />
      <div class="spec-poll__options">
        ${poll.options.map((option) => {
          const count = poll.votes.filter((vote) => vote.optionId === option.id).length;
          const percent = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const selected = poll.votes.some((vote) => vote.viewerId === viewerId && vote.optionId === option.id);
          return `
            <div class="spec-poll-option ${canEdit ? "" : "spec-poll-option--vote-only"}">
              <button
                type="button"
                class="spec-poll-option__vote ${selected ? "is-selected" : ""}"
                data-spec-poll-vote="${escapeHtml(poll.id)}"
                data-spec-poll-entity="${escapeHtml(entityId)}"
                data-spec-poll-option="${escapeHtml(option.id)}"
                aria-pressed="${selected ? "true" : "false"}"
              >
                <span class="spec-poll-option__bar" style="width:${percent}%"></span>
                <span data-spec-poll-option-display="${escapeHtml(option.id)}">${escapeHtml(option.label)}</span>
                <strong>${percent}%</strong>
              </button>
              ${canEdit ? `<input
                  data-spec-poll-option-label="${escapeHtml(poll.id)}"
                  data-spec-poll-entity="${escapeHtml(entityId)}"
                  data-spec-poll-option="${escapeHtml(option.id)}"
                  value="${escapeHtml(option.label)}"
                  aria-label="Poll option label"
                />
                <button
                  type="button"
                  data-remove-spec-poll-option="${escapeHtml(poll.id)}"
                  data-spec-poll-entity="${escapeHtml(entityId)}"
                  data-spec-poll-option="${escapeHtml(option.id)}"
                  ${poll.options.length <= 2 ? "disabled" : ""}
                  title="Remove option"
                  aria-label="Remove option"
                >-</button>` : ""}
            </div>
          `;
        }).join("")}
      </div>
      <div class="spec-poll__foot">
        <span>${totalVotes} ${totalVotes === 1 ? "vote" : "votes"}</span>
        ${canEdit ? `<button
            type="button"
            data-add-spec-poll-option="${escapeHtml(poll.id)}"
            data-spec-poll-entity="${escapeHtml(entityId)}"
          >Add option</button>` : ""}
      </div>
    </article>
  `;
};

const defaultSpec = (entity: SyncrelEntity): string => `# ${entity.name}

## Context
Describe the customer, business, or product situation this card represents.

## Proposal
- What should change
- How the framework fields should shape the decision
- What evidence is still missing

## Open Questions
- What needs validation before build
`;

const renderOutline = (doc: string): string => {
  const headings = doc
    .split(/\r?\n/)
    .map((line) => line.match(/^(#{1,3})\s+(.+)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map((match) => ({ level: match[1].length, title: match[2].trim() }))
    .filter((heading) => heading.title.length > 0);

  if (headings.length === 0) return `<span class="spec-outline__empty">No headings yet</span>`;
  return headings.map((heading, index) => `
    <button type="button" data-spec-outline="${index}" style="--outline-depth:${heading.level - 1};">
      <span>H${heading.level}</span>
      ${escapeHtml(heading.title)}
    </button>
  `).join("");
};
